import type { App } from 'vue';

import axios from 'axios';

import { globalState, simpleObservableStore } from 'boot/simpleObservableStore';
import { DBSchema } from 'idb';
import { useAuthenticationStore } from 'src/stores/authentication';

import { InstanceAuthenticationConfiguration } from 'src/models/authentication';

import { INSTANCE_AUTHENTICATION_CONFIGURATION_KEY } from 'src/services/authentication';

import useAuthentication from 'src/filters/authentication';

import { CommandBuilder } from 'src/util/command/CommandBuilder';
import { CommandBus } from 'src/util/command/CommandBus';
import { urlForInstance } from 'src/util/common';
import { EventBus } from 'src/util/eventBus/EventBus';
import { MessageBus } from 'src/util/messagebus/MessageBus';
import { QueryBuilder } from 'src/util/query/QueryBuilder';
import { QueryStore } from 'src/util/query/QueryStore';
import { QueryStoreFactory } from 'src/util/query/QueryStoreFactory';
import { PongConfiguration } from 'src/util/socket/types/PongConfiguration';

declare module '@vue/runtime-core' {
	interface ComponentCustomProperties {
		$commandBus: CommandBus;
		$queryStore: QueryStore;
		$queryStoreFactory: QueryStoreFactory;
	}
}

import { v4 as uuidV4 } from 'uuid';

let activeQueryStore: QueryStore | null = null;
let activeQueryStoreFactory: QueryStoreFactory | null = null;

export interface InfraStrukturDB extends DBSchema {
	query: {
		indexes: { keys: string[] };
		key: string;
		value: unknown;
	};
}

export async function bootQueryStoreAndCommandBus({ app }: { app: App<Element> }): Promise<void> {
	const { initialize } = useAuthentication();
	const authenticationStore = useAuthenticationStore();

	initialize();

	let activeInstanceAuthenticationConfiguration: InstanceAuthenticationConfiguration | null =
		authenticationStore.activeInstanceAuthenticationConfiguration;
	if (activeInstanceAuthenticationConfiguration === null || activeInstanceAuthenticationConfiguration === undefined) {
		simpleObservableStore.bootHealth = 'authLost';
		return;
	}

	if (
		typeof activeInstanceAuthenticationConfiguration.user !== 'string' ||
		activeInstanceAuthenticationConfiguration.user === ''
	) {
		simpleObservableStore.bootHealth = 'authLost';
		return;
	}

	const eventBus: EventBus = globalState.eventBus as EventBus;

	const storedInstanceAuthenticationConfigurationFor = (
		instanceAuthenticationConfiguration: InstanceAuthenticationConfiguration
	): InstanceAuthenticationConfiguration | undefined => {
		const allInstanceAuthenticationConfigurationsSerialized = localStorage.getItem(
			INSTANCE_AUTHENTICATION_CONFIGURATION_KEY
		);
		if (allInstanceAuthenticationConfigurationsSerialized === null) {
			return undefined;
		}

		try {
			const allInstanceAuthenticationConfigurationsData = JSON.parse(
				allInstanceAuthenticationConfigurationsSerialized
			) as Array<InstanceAuthenticationConfiguration>;

			return allInstanceAuthenticationConfigurationsData.find(
				(storedInstanceAuthenticationConfiguration) =>
					storedInstanceAuthenticationConfiguration !== null &&
					typeof storedInstanceAuthenticationConfiguration.name === 'string' &&
					storedInstanceAuthenticationConfiguration.name === instanceAuthenticationConfiguration.name &&
					typeof storedInstanceAuthenticationConfiguration.user === 'string' &&
					storedInstanceAuthenticationConfiguration.user !== ''
			);
		} catch {
			return undefined;
		}
	};

	const provideCommandBusFromInstanceConfiguration = (
		instanceAuthenticationConfiguration: InstanceAuthenticationConfiguration
	): void => {
		const storedInstanceAuthenticationConfiguration = storedInstanceAuthenticationConfigurationFor(
			instanceAuthenticationConfiguration
		);
		if (storedInstanceAuthenticationConfiguration === undefined) {
			return;
		}

		const commandBuilder = new CommandBuilder(instanceAuthenticationConfiguration.url);
		const commandBus = new CommandBus(
			commandBuilder,
			axios,
			null,
			instanceAuthenticationConfiguration.name,
			storedInstanceAuthenticationConfiguration.user,
			process.env.VERSION
		);

		app.config.globalProperties.$commandBus = commandBus;
		app.provide('commandBus', commandBus);
	};

	const provideHttpOnlyQueryStoreFromInstanceConfiguration = (
		instanceAuthenticationConfiguration: InstanceAuthenticationConfiguration
	): void => {
		const storedInstanceAuthenticationConfiguration = storedInstanceAuthenticationConfigurationFor(
			instanceAuthenticationConfiguration
		);
		if (storedInstanceAuthenticationConfiguration === undefined) {
			return;
		}

		activeQueryStore?.dispose();
		activeQueryStoreFactory?.dispose();
		activeQueryStore = null;
		activeQueryStoreFactory = null;

		const messageBus = {
			dispose: () => undefined,
			publish: () => undefined,
			subscribe: () => undefined,
			unsubscribe: () => undefined,
		} as unknown as MessageBus;
		const queryStore = new QueryStore(
			new QueryBuilder(instanceAuthenticationConfiguration.url),
			messageBus,
			eventBus,
			axios,
			null,
			instanceAuthenticationConfiguration.name,
			storedInstanceAuthenticationConfiguration.user,
			process.env.VERSION,
			false
		);

		activeQueryStore = queryStore;

		app.provide('messageBus', messageBus);
		app.provide('queryStore', queryStore);
		app.config.globalProperties.$queryStore = queryStore;
	};

	const requestPongConfiguration = async (
		instanceAuthenticationConfiguration: InstanceAuthenticationConfiguration
	): Promise<PongConfiguration> => {
		const url = `${instanceAuthenticationConfiguration.url}/workspace/index/get-pong-configuration/format/json`;
		const backoffsMs = [0, 250, 750];
		let lastError: unknown;
		for (const delay of backoffsMs) {
			if (delay > 0) {
				await new Promise<void>((resolve) => setTimeout(resolve, delay));
			}
			try {
				const response = await axios.get<PongConfiguration>(url);
				if (!Object.prototype.hasOwnProperty.call(response.data, 'pongHost')) {
					lastError = new Error('Pong response missing pongHost');
					continue;
				}
				return response.data;
			} catch (err: unknown) {
				lastError = err;
			}
		}
		throw lastError instanceof Error ? lastError : new Error('Pong fetch failed');
	};

	const markPongFallbackHealth = (): void => {
		if (simpleObservableStore.bootHealth !== 'authLost') {
			simpleObservableStore.bootHealth = 'pongFailed';
		}
	};

	const persistActiveInstanceAuthenticationConfigurationUrl = (
		instanceAuthenticationConfiguration: InstanceAuthenticationConfiguration,
		url: string
	): InstanceAuthenticationConfiguration => {
		const updatedInstanceAuthenticationConfiguration = {
			...instanceAuthenticationConfiguration,
			url,
		};
		const serializedInstanceAuthenticationConfigurations = localStorage.getItem(
			INSTANCE_AUTHENTICATION_CONFIGURATION_KEY
		);
		let allInstanceAuthenticationConfigurations: Array<InstanceAuthenticationConfiguration> = [];

		try {
			allInstanceAuthenticationConfigurations =
				serializedInstanceAuthenticationConfigurations !== null
					? (JSON.parse(
							serializedInstanceAuthenticationConfigurations
						) as Array<InstanceAuthenticationConfiguration>)
					: [];
		} catch {
			allInstanceAuthenticationConfigurations = [];
		}

		const instanceAuthenticationConfigurationExists = allInstanceAuthenticationConfigurations.some(
			(storedInstanceAuthenticationConfiguration) =>
				storedInstanceAuthenticationConfiguration.name.toLowerCase() ===
				instanceAuthenticationConfiguration.name.toLowerCase()
		);
		const updatedInstanceAuthenticationConfigurations = instanceAuthenticationConfigurationExists
			? allInstanceAuthenticationConfigurations.map((storedInstanceAuthenticationConfiguration) =>
					storedInstanceAuthenticationConfiguration.name.toLowerCase() ===
					instanceAuthenticationConfiguration.name.toLowerCase()
						? {
								...storedInstanceAuthenticationConfiguration,
								url,
							}
						: storedInstanceAuthenticationConfiguration
				)
			: [...allInstanceAuthenticationConfigurations, updatedInstanceAuthenticationConfiguration];

		authenticationStore.setInstanceAuthenticationConfigurations(updatedInstanceAuthenticationConfigurations);
		authenticationStore.setActiveInstanceAuthenticationConfiguration(updatedInstanceAuthenticationConfiguration);

		return updatedInstanceAuthenticationConfiguration;
	};

	const initializeQueryStoreFromPongConfiguration = (
		instanceAuthenticationConfiguration: InstanceAuthenticationConfiguration,
		pongConfiguration: PongConfiguration
	): void => {
		if (simpleObservableStore.showVersionMismatch) {
			return;
		}

		if (!Object.prototype.hasOwnProperty.call(pongConfiguration, 'pongHost')) {
			throw new Error('Pong response missing pongHost — likely transient');
		}

		pongConfiguration.pongHost = pongConfiguration.pongHost.replace('https', 'wss');

		pongConfiguration.pongId = uuidV4();

		globalState.pongConfiguration = pongConfiguration;

		activeQueryStore?.dispose();
		activeQueryStoreFactory?.dispose();
		activeQueryStore = null;
		activeQueryStoreFactory = null;

		const queryStoreFactory = new QueryStoreFactory(
			instanceAuthenticationConfiguration.name,
			instanceAuthenticationConfiguration.url,
			pongConfiguration,
			null,
			eventBus
		);

		const messageBus = queryStoreFactory.getMessageBus();

		const queryStore = queryStoreFactory.create();
		activeQueryStoreFactory = queryStoreFactory;
		activeQueryStore = queryStore;

		const storedInstanceAuthenticationConfiguration = storedInstanceAuthenticationConfigurationFor(
			instanceAuthenticationConfiguration
		);
		if (storedInstanceAuthenticationConfiguration !== undefined) {
			const commandBuilder = new CommandBuilder(instanceAuthenticationConfiguration.url);
			const commandBus = new CommandBus(
				commandBuilder,
				axios,
				null,
				instanceAuthenticationConfiguration.name,
				storedInstanceAuthenticationConfiguration.user,
				process.env.VERSION
			);

			app.config.globalProperties.$commandBus = commandBus;
			app.provide('commandBus', commandBus);
		}

		app.provide('queryStoreFactory', queryStoreFactory);
		app.provide('queryStore', queryStore);
		app.provide('messageBus', messageBus);

		app.config.globalProperties.$queryStore = queryStore;
		app.config.globalProperties.$queryStoreFactory = queryStoreFactory;
	};

	try {
		initializeQueryStoreFromPongConfiguration(
			activeInstanceAuthenticationConfiguration,
			await requestPongConfiguration(activeInstanceAuthenticationConfiguration)
		);
		return;
	} catch (exception) {
		if (simpleObservableStore.showVersionMismatch) {
			return;
		}

		console.warn('Could not load pong configuration for active instance.', exception);
	}

	if (activeInstanceAuthenticationConfiguration.url !== '') {
		markPongFallbackHealth();
		provideCommandBusFromInstanceConfiguration(activeInstanceAuthenticationConfiguration);
		provideHttpOnlyQueryStoreFromInstanceConfiguration(activeInstanceAuthenticationConfiguration);
		return;
	}

	const resolvedActiveInstanceUrl = await urlForInstance(activeInstanceAuthenticationConfiguration.name).catch(
		() => ''
	);

	if (
		resolvedActiveInstanceUrl === '' ||
		resolvedActiveInstanceUrl === activeInstanceAuthenticationConfiguration.url
	) {
		markPongFallbackHealth();
		provideCommandBusFromInstanceConfiguration(activeInstanceAuthenticationConfiguration);
		provideHttpOnlyQueryStoreFromInstanceConfiguration(activeInstanceAuthenticationConfiguration);
		return;
	}

	activeInstanceAuthenticationConfiguration = persistActiveInstanceAuthenticationConfigurationUrl(
		activeInstanceAuthenticationConfiguration,
		resolvedActiveInstanceUrl
	);

	try {
		initializeQueryStoreFromPongConfiguration(
			activeInstanceAuthenticationConfiguration,
			await requestPongConfiguration(activeInstanceAuthenticationConfiguration)
		);
	} catch (exception) {
		if (!simpleObservableStore.showVersionMismatch) {
			console.warn('Could not load pong configuration after resolving active instance URL.', exception);
		}
		markPongFallbackHealth();
		provideCommandBusFromInstanceConfiguration(activeInstanceAuthenticationConfiguration);
		provideHttpOnlyQueryStoreFromInstanceConfiguration(activeInstanceAuthenticationConfiguration);
	}
}
